import React from "react";
import {
  Trophy,
  Award,
  Medal,
  Star,
  Clock,
  Heart,
  CheckCircle2,
  Lock,
  Target,
  Flame,
  Zap,
  Shield,
  BookOpen,
  Users,
  BarChart3,
  Flag,
  Check,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type BadgeCategory = "hours" | "tips" | "tasks" | "rating" | "milestone" | "general";

interface BadgeData {
  id?: number;
  name: string;
  description?: string;
  icon?: string;
  awarded_at?: string;
  awardedAt?: string;
  badge?: BadgeData;
  criteria_type?: string;
  criteria_value?: number;
  category?: string;
}

type BadgeSize = "sm" | "md" | "lg";

interface BadgeDisplayProps {
  badge: BadgeData;
  size?: BadgeSize;
  showLabel?: boolean;
  showDescription?: boolean;
  showAwardedDate?: boolean;
  earned?: boolean;
  onClick?: () => void;
  className?: string;
}

type LucideIconProps = { size?: number; strokeWidth?: number; color?: string; className?: string };

const CATEGORY_ICONS: Record<string, React.ComponentType<LucideIconProps>> = {
  hours: Clock,
  tips: Heart,
  tasks: CheckCircle2,
  rating: Star,
  milestone: Flag,
  general: Trophy,
};

// Map icon string to Lucide component
function getIconComponent(iconStr?: string): React.ComponentType<LucideIconProps> {
  if (!iconStr) return Trophy;
  const map: Record<string, React.ComponentType<LucideIconProps>> = {
    trophy: Trophy,
    award: Award,
    medal: Medal,
    star: Star,
    clock: Clock,
    "clock-9": Clock,
    heart: Heart,
    "heart-handshake": Heart,
    check: CheckCircle2,
    "check-circle": CheckCircle2,
    target: Target,
    flag: Flag,
    flame: Flame,
    zap: Zap,
    shield: Shield,
    book: BookOpen,
    users: Users,
    chart: BarChart3,
  };
  return map[iconStr.toLowerCase()] ?? Trophy;
}

const SIZE_CONFIG = {
  sm: { iconSize: 18, iconStroke: 1.8, tile: "size-10", label: "text-caption", padding: "p-3" },
  md: { iconSize: 22, iconStroke: 1.6, tile: "size-12", label: "text-sm", padding: "p-4" },
  lg: { iconSize: 28, iconStroke: 1.5, tile: "size-14", label: "text-sm", padding: "p-5" },
} as const;

const MEDAL_LABELS: Record<number, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

// Medal ring for leaderboard podium ranks
function MedalRing({ rank, className }: { rank: number; className?: string }) {
  const label = MEDAL_LABELS[rank];
  if (!label) return null;

  return (
    <span
      className={cn(
        "inline-flex size-9 shrink-0 items-center justify-center rounded-full border text-caption font-medium tabular-nums",
        rank === 1
          ? "border-warning/40 bg-warning/15 text-warning"
          : "border-hairline-strong bg-muted text-foreground",
        className,
      )}
      aria-label={`Rank ${rank}`}
    >
      {label}
    </span>
  );
}

// Small circular rank badge for leaderboard rows
function RankBadge({ rank, className }: { rank: number; className?: string }) {
  if (rank <= 3) return <MedalRing rank={rank} className={className} />;
  return (
    <span
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background font-mono text-caption text-muted-foreground tabular-nums",
        className,
      )}
      aria-label={`Rank ${rank}`}
    >
      #{rank}
    </span>
  );
}

export { MedalRing, RankBadge };

export function BadgeDisplay({
  badge,
  size = "md",
  showLabel = true,
  showDescription = false,
  showAwardedDate = false,
  earned = true,
  onClick,
  className,
}: BadgeDisplayProps) {
  const cfg = SIZE_CONFIG[size];
  const IconComp = getIconComponent(badge.icon || badge.category);
  const name = badge.name || "Badge";
  const description = badge.description || "";
  const awardedAt = badge.awarded_at || badge.awardedAt || "";

  const inner = (
    <>
      {/* Icon tile */}
      <span
        className={cn(
          "relative flex items-center justify-center rounded-md border",
          cfg.tile,
          earned
            ? "border-hairline-strong bg-muted text-foreground"
            : "border-border bg-background text-muted-foreground opacity-50",
        )}
        aria-hidden
      >
        <IconComp size={cfg.iconSize} strokeWidth={cfg.iconStroke} />
        <span
          className={cn(
            "absolute -right-1.5 -bottom-1.5 flex size-4 items-center justify-center rounded-full border-2 border-card",
            earned ? "bg-success text-background" : "bg-muted-foreground text-background",
          )}
        >
          {earned ? (
            <Check size={9} strokeWidth={3} />
          ) : (
            <Lock size={8} strokeWidth={2.5} />
          )}
        </span>
      </span>

      {showLabel && (
        <span
          className={cn(
            "max-w-[140px] leading-snug font-medium",
            cfg.label,
            earned ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {name}
        </span>
      )}

      {showDescription && description && (
        <span className="line-clamp-3 max-w-[160px] text-caption leading-snug text-muted-foreground">
          {description}
        </span>
      )}

      {showAwardedDate && awardedAt && (
        <Badge variant="success">
          <Trophy aria-hidden />
          {new Date(awardedAt).toLocaleDateString()}
        </Badge>
      )}
    </>
  );

  const base = cn(
    "flex w-full flex-col items-center gap-2 text-center",
    cfg.padding,
    className,
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className={cn(
              base,
              "rounded-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {inner}
          </button>
        ) : (
          <div className={base} tabIndex={0}>
            {inner}
          </div>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {description || name}
      </TooltipContent>
    </Tooltip>
  );
}

// Grid variant for badge collections
interface BadgeGridProps {
  badges: BadgeData[];
  size?: BadgeSize;
  showDescription?: boolean;
  showAwardedDate?: boolean;
  earnedBadgeIds?: number[];
  /** Column spans out of 12 per breakpoint (MUI Grid style): 12 = one per row, 3 = four per row. */
  cols?: { xs?: number; sm?: number; md?: number; lg?: number };
  onBadgeClick?: (badge: BadgeData) => void;
  className?: string;
}

const perRow = (span: number | undefined, fallback: number) =>
  Math.max(1, Math.round(12 / (span || fallback)));

export function BadgeGrid({
  badges,
  size = "md",
  showDescription = false,
  showAwardedDate = false,
  earnedBadgeIds,
  cols = { xs: 12, sm: 6, md: 4, lg: 3 },
  onBadgeClick,
  className,
}: BadgeGridProps) {
  const style = {
    "--bg-xs": perRow(cols.xs, 12),
    "--bg-sm": perRow(cols.sm, 6),
    "--bg-md": perRow(cols.md, 4),
    "--bg-lg": perRow(cols.lg, 3),
  } as React.CSSProperties;

  return (
    <div
      style={style}
      className={cn(
        "grid gap-3 grid-cols-[repeat(var(--bg-xs),minmax(0,1fr))] sm:grid-cols-[repeat(var(--bg-sm),minmax(0,1fr))] md:grid-cols-[repeat(var(--bg-md),minmax(0,1fr))] lg:grid-cols-[repeat(var(--bg-lg),minmax(0,1fr))]",
        className,
      )}
    >
      {badges.map((badge) => {
        const isEarned =
          earnedBadgeIds !== undefined ? earnedBadgeIds.includes(badge.id || 0) : true;
        return (
          <div
            key={badge.id || badge.name}
            className="overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-hairline-strong"
          >
            <BadgeDisplay
              badge={badge}
              size={size}
              showDescription={showDescription}
              showAwardedDate={showAwardedDate}
              earned={isEarned}
              onClick={onBadgeClick ? () => onBadgeClick(badge) : undefined}
            />
          </div>
        );
      })}
    </div>
  );
}

// Progress bar toward a specific badge
interface BadgeProgressProps {
  badge: BadgeData;
  current: number;
  className?: string;
}

export function BadgeProgress({ badge, current, className }: BadgeProgressProps) {
  const target = badge.criteria_value || 1;
  const pct = Math.min(100, Math.round((current / target) * 100));
  const IconComp = getIconComponent(badge.icon || badge.category);

  return (
    <div className={cn("mb-4", className)}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <IconComp size={14} strokeWidth={2} className="shrink-0 text-muted-foreground" />
          <span className="truncate text-[13px] font-medium text-foreground">{badge.name}</span>
        </span>
        <span
          className={cn(
            "font-mono text-caption tabular-nums",
            pct >= 100 ? "text-success" : "text-muted-foreground",
          )}
        >
          {current} / {target}
        </span>
      </div>
      <Progress
        value={pct}
        aria-label={`${badge.name} progress`}
        className={cn(pct >= 100 && "[&>div]:bg-success")}
      />
    </div>
  );
}
